import { Link } from "wouter";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

export default function Help() {
  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title="Help & FAQ"
        description="Find answers to common questions about working with AccelerateByte, our delivery timelines, pricing, and technical expertise."
      />
      <JsonLd schema={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How quickly can AccelerateByte start a project?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Depending on team availability, we can typically kick off Discovery & Scoping within 3-5 business days of signing the agreement. We pride ourselves on zero bureaucratic friction."
            }
          },
          {
            "@type": "Question",
            "name": "Does AccelerateByte work internationally?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. While headquartered in Mumbai, 80% of our clients are based in the US, UK, EU, and UAE. We overlap hours to ensure synchronous communication."
            }
          },
          {
            "@type": "Question",
            "name": "How does onboarding work with AccelerateByte?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Once contracted, you will be introduced to your dedicated Lead Engineer. We set up a Slack/Discord channel, grant access to our project management board, and schedule a kickoff call to finalise technical requirements."
            }
          },
          {
            "@type": "Question",
            "name": "Does AccelerateByte sign NDAs?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. We understand the importance of IP protection and are happy to sign standard NDAs before any code is shared or detailed discussions take place."
            }
          },
          {
            "@type": "Question",
            "name": "How much does a website or app cost at AccelerateByte?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Every project is unique. An MVP might start around $5,000–$10,000, while complex enterprise platforms scale higher. Our focus is ROI — delivering systems that make or save you more than they cost."
            }
          },
          {
            "@type": "Question",
            "name": "How long does it take AccelerateByte to deliver a project?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We specialise in rapid deployment. Simple marketing sites take 1-2 weeks. Complex MVPs take 4-8 weeks. We provide guaranteed timelines during the scoping phase."
            }
          },
          {
            "@type": "Question",
            "name": "Does AccelerateByte offer fixed-price or hourly pricing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We generally prefer fixed-price milestone contracts for defined projects like an MVP. For ongoing development or staff augmentation, we offer monthly retainers."
            }
          },
          {
            "@type": "Question",
            "name": "What technologies does AccelerateByte use?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We specialise in the modern JavaScript/TypeScript ecosystem: React, Next.js, Node.js, React Native, Tailwind CSS, PostgreSQL, and cloud deployments on AWS and Vercel."
            }
          },
          {
            "@type": "Question",
            "name": "Can AccelerateByte work with an existing codebase?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, provided the stack aligns with our expertise. We typically start with a code audit to assess technical debt before committing to feature development on legacy systems."
            }
          },
          {
            "@type": "Question",
            "name": "What happens after launch with AccelerateByte?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We offer 30 days of free bug-fix support post-launch. After that, clients typically move to a Maintenance & Support retainer for uptime monitoring, security updates, and continued feature iterations."
            }
          }
        ]
      }} />
      <section className="pt-32 pb-16 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 tracking-tight">Frequently Asked Questions</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about working with AccelerateByte.
          </p>
        </div>
      </section>

      <section className="py-12 px-4 container mx-auto max-w-3xl mb-24">
        <div className="space-y-12">
          {/* Section 1 */}
          <div>
            <h2 className="font-display text-2xl font-bold mb-6 text-primary">Working with us</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left font-medium">How quickly can you start?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Depending on team availability, we can typically kick off Discovery & Scoping within 3-5 business days of signing the agreement. We pride ourselves on zero bureaucratic friction.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left font-medium">Do you work internationally?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes. While we are headquartered in Mumbai, 80% of our clients are based in the US, UK, EU, and UAE. We overlap hours to ensure synchronous communication.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left font-medium">How does onboarding work?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Once contracted, you'll be introduced to your dedicated Lead Engineer. We set up a Slack/Discord channel, grant access to our project management board, and schedule a kickoff call to finalize technical requirements.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-left font-medium">Do you sign NDAs?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Absolutely. We understand the importance of IP protection and are happy to sign standard NDAs before any code is shown or detailed discussions take place.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="font-display text-2xl font-bold mb-6 text-primary">Pricing & timelines</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-5">
                <AccordionTrigger className="text-left font-medium">How much does a website or app cost?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Every project is unique. An MVP might start around $5,000-$10,000, while complex enterprise platforms scale higher. Our focus is ROI—delivering systems that make or save you more money than they cost.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6">
                <AccordionTrigger className="text-left font-medium">How long does it take?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We specialize in rapid deployment. Simple marketing sites take 1-2 weeks. Complex MVPs take 4-8 weeks. We provide guaranteed timelines during the scoping phase.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-7">
                <AccordionTrigger className="text-left font-medium">Fixed vs hourly pricing?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We generally prefer fixed-price milestone contracts for defined projects (like an MVP). For ongoing development or staff augmentation, we offer monthly retainers.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="font-display text-2xl font-bold mb-6 text-primary">Technical</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-8">
                <AccordionTrigger className="text-left font-medium">What technologies do you use?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We are specialized in the modern JavaScript/TypeScript ecosystem. React, Next.js, Node.js, React Native, Tailwind CSS, PostgreSQL, and cloud deployments on AWS/Vercel.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-9">
                <AccordionTrigger className="text-left font-medium">Can you work with an existing codebase?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes, provided the stack aligns with our expertise. We typically start with a code audit to assess technical debt before committing to feature development on legacy systems.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-10">
                <AccordionTrigger className="text-left font-medium">What happens after launch?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We offer 30 days of free bug-fix support post-launch. After that, clients usually move to a Maintenance & Support retainer to ensure uptime, security updates, and continued feature iterations.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <div className="mt-16 bg-muted/20 border border-border/50 rounded-2xl p-8 text-center">
          <h3 className="font-display text-xl font-bold mb-4">Still have questions?</h3>
          <p className="text-muted-foreground mb-6">Send us a message and we'll get back to you with the answers.</p>
          <Link href="/contact" className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
